import { Stack, styled } from '@mui/material';
import { Calendar, DnaHelix, People, Pill, Stethoscope } from '~/assets/icons';
import { theme } from '~/shared/styles/theme';
import type { SearchCohortEntriesQuery } from '~/graphql/gen/graphql';
import CohortOverviewCard from '~/modules/sponsor/components/CohortOverviewCard';
import CohortResultsSection from '~/modules/sponsor/components/CohortResultsSection';
import BreakdownTableCard from '~/modules/sponsor/components/BreakdownTableCard';

type CohortResult = NonNullable<
  SearchCohortEntriesQuery['searchCohortEntries']
>;

export interface Props {
  result: CohortResult;
  onClearFilters: () => void;
}

const SponsorDashboardResultsState = ({ result, onClearFilters }: Props) => (
  <ResultsSection>
    <CohortOverviewCard
      totalCohortSize={result.totalCohortSize}
      filtersApplied={result.filtersApplied}
      onClear={onClearFilters}
    />
    <SectionsStack>
      <CohortResultsSection
        title="Demographics"
        icon={<People width="30" height="30" />}
      >
        {result.censusRegion && (
          <BreakdownTableCard
            title="Census Region"
            columnHeader="REGION"
            breakdown={result.censusRegion}
          />
        )}
        {result.ageBand && (
          <BreakdownTableCard
            title="Age Band"
            columnHeader="AGE BAND"
            breakdown={result.ageBand}
          />
        )}
        {result.sex && (
          <BreakdownTableCard
            title="Sex Assigned at Birth"
            columnHeader="SEX ASSIGNED AT BIRTH"
            breakdown={result.sex}
          />
        )}
        {result.raceEthnicity && (
          <BreakdownTableCard
            title="Race/Ethnicity"
            columnHeader="RACE/ETHNICITY"
            breakdown={result.raceEthnicity}
          />
        )}
      </CohortResultsSection>

      <CohortResultsSection
        title="Genetic Information"
        icon={<DnaHelix width="30" height="30" />}
      >
        {result.geneSymbol && (
          <BreakdownTableCard
            title="Gene Symbol"
            columnHeader="GENE SYMBOL"
            breakdown={result.geneSymbol}
            skipFormatting
          />
        )}
        {result.testType && (
          <BreakdownTableCard
            title="Test Type"
            columnHeader="TEST TYPE"
            breakdown={result.testType}
          />
        )}
        {result.labSource && (
          <BreakdownTableCard
            title="Lab Source"
            columnHeader="LAB SOURCE"
            breakdown={result.labSource}
          />
        )}
        {result.inheritancePattern && (
          <BreakdownTableCard
            title="Inheritance Pattern"
            columnHeader="PATTERN"
            breakdown={result.inheritancePattern}
          />
        )}
      </CohortResultsSection>

      <CohortResultsSection
        title="Clinical Information"
        icon={<Stethoscope width="30" height="30" />}
      >
        {result.diagnoses && (
          <BreakdownTableCard
            title="Diagnoses"
            columnHeader="DIAGNOSES"
            breakdown={result.diagnoses}
          />
        )}
        {result.keySymptoms && (
          <BreakdownTableCard
            title="Key Symptoms"
            columnHeader="SYMPTOMS"
            breakdown={result.keySymptoms}
          />
        )}
        {result.keyLabs && (
          <BreakdownTableCard
            title="Key Labs"
            columnHeader="LABS"
            breakdown={result.keyLabs}
          />
        )}
        {result.onsetType && (
          <BreakdownTableCard
            title="Onset Type"
            columnHeader="TYPE"
            breakdown={result.onsetType}
          />
        )}
      </CohortResultsSection>

      <CohortResultsSection
        title="Treatment & Care"
        icon={<Pill width="30" height="30" />}
      >
        {result.treatmentExposure && (
          <BreakdownTableCard
            title="Treatment Exposure"
            columnHeader="EXPOSURE"
            breakdown={result.treatmentExposure}
          />
        )}
      </CohortResultsSection>

      <CohortResultsSection
        title="Temporal"
        icon={<Calendar width="30" height="30" />}
      >
        {result.yearOfSample && (
          <BreakdownTableCard
            title="Year of Sample"
            columnHeader="SAMPLE YEAR"
            breakdown={result.yearOfSample}
          />
        )}
      </CohortResultsSection>
    </SectionsStack>
  </ResultsSection>
);

export default SponsorDashboardResultsState;

const ResultsSection = styled(Stack)(() => ({
  gap: theme.spacing(3),
}));

const SectionsStack = styled(Stack)(() => ({
  gap: theme.spacing(4),
}));
